import { LoadingOutlined } from '@ant-design/icons';
import { Button, Modal, UploadFile, message } from 'antd';
import React, { memo, useState, useCallback, useEffect } from 'react';
import { ModalKey } from '@xbcb/ui-types';
import { TextSearchOperator } from '@xbcb/shared-types';
import { reportError, useModal } from '@xbcb/ui-utils';
import { AdditionalTariffSpreadsheet } from 'libs/htsHelpers';
import ImportAdditionalTariffUpload from 'components/ImportAdditionalTariffUpload';
import {
  StyledSteps,
  SaveContainer,
  SaveTitle,
  ReadyText,
  DescriptionText,
  LoadingContainer,
  LoadingIcon,
  ResultContainer,
  SuccessText,
  ErrorText,
  UpdatedCountText,
  WarningText,
  ErrorDetailsText,
  CloseButton,
} from './styles';
import ImportAdditionalTariffReview from 'components/ImportAdditionalTariffReview';
import { updateHtsRecordsWithAdditionalTariff } from 'libs/tariffs/updateAdditionalHtssApplicable';
import { client as apolloClient } from '@xbcb/apollo-client';
import { gql } from '@apollo/client';
import {
  UsHts,
  searchUsHtssQuery,
  searchUsHtssQueryVariables,
} from '__generated__/graphql';

const SEARCH_US_HTS = gql`
  query searchUsHtss($input: SearchUsHtsInput!) {
    searchUsHtss(input: $input) {
      results {
        id
        version
        number
        additionalHtssApplicable {
          number
          type
          startDate
          exportGroup
          additionalTypeDescription
        }
      }
    }
  }
`;

const PROCESSING_SPREADSHEETS_MESSAGE =
  'Processing spreadsheets, this may take a minute.';

const APPLYING_TARIFFS_MESSAGE =
  'Applying additional tariffs to HTS records, this may take a minute.';

export enum ImportAdditionalTariffSteps {
  UPLOAD,
  REVIEW,
  SAVE,
}

type ProcessedTariffData = {
  key: string;
  additionalNumber: string;
  count: number;
};

type ImportResult = {
  success: boolean;
  updatedCount: number;
  errors: string[];
};

const importSteps = [
  { title: 'Upload' },
  { title: 'Review' },
  { title: 'Save' },
];

type TariffData = {
  prefix: string;
  tariff: string;
  type?: string;
  startDate?: string;
  description?: string;
  exportGroup?: string;
};

const extractSpreadsheetData = (
  additionalTariffSpreadsheets: AdditionalTariffSpreadsheet[] | undefined,
): TariffData[] => {
  if (!additionalTariffSpreadsheets?.length) return [];

  const rawData: TariffData[] = [];
  additionalTariffSpreadsheets.forEach((spreadsheet) => {
    spreadsheet.rows?.slice(1).forEach((row) => {
      const prefix = (row['A'] || row[0] || Object.values(row)[0])
        ?.toString()
        .trim();
      const tariff = (row['B'] || row[1] || Object.values(row)[1])
        ?.toString()
        .trim();
      const type = (row['C'] || row[2] || Object.values(row)[2])
        ?.toString()
        .trim();
      const startDate = (row['D'] || row[3] || Object.values(row)[3])
        ?.toString()
        .trim();
      const description = (row['E'] || row[4] || Object.values(row)[4])
        ?.toString()
        .trim();
      const exportGroup = (row['F'] || row[5] || Object.values(row)[5])
        ?.toString()
        .trim();

      if (prefix && tariff) {
        rawData.push({
          prefix,
          tariff,
          type: type || undefined,
          startDate: startDate || undefined,
          description: description || undefined,
          exportGroup: exportGroup || undefined,
        });
      }
    });
  });
  return rawData;
};

/**
 * ImportAdditionalTariffsModal
 *
 * Modal that allows user to upload additional tariffs from excel files.
 */

export const ImportAdditionalTariffModal: React.FC = () => {
  const { visible, closeModal } = useModal(ModalKey.IMPORT_ADDITIONAL_TARIFF);

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      resetModalState();
    }
  }, [visible]);

  const [isLoading, setIsLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [additionalTariffSpreadsheets, setAdditionalTariffSpreadsheets] =
    useState<AdditionalTariffSpreadsheet[] | undefined>(undefined);
  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const [currentStep, setCurrentStep] = useState<ImportAdditionalTariffSteps>(
    ImportAdditionalTariffSteps.UPLOAD,
  );
  const [processedData, setProcessedData] = useState<ProcessedTariffData[]>([]);
  const [rawSpreadsheetData, setRawSpreadsheetData] = useState<TariffData[]>(
    [],
  );
  const [htsRecords, setHtsRecords] = useState<UsHts[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const resetModalState = () => {
    setCurrentStep(ImportAdditionalTariffSteps.UPLOAD);
    setImportResult(null);
    setIsLoading(false);
    setFileList([]);
    setAdditionalTariffSpreadsheets(undefined);
    setIsPreviewReady(false);
    setProcessedData([]);
    setRawSpreadsheetData([]);
    setHtsRecords([]);
  };

  const handleCancel = () => {
    if (currentStep === ImportAdditionalTariffSteps.UPLOAD) {
      resetModalState();
      closeModal();
    } else {
      setCurrentStep(ImportAdditionalTariffSteps.UPLOAD);
    }
  };

  const searchHtsRecordsByPrefixes = useCallback(
    async (prefixes: string[]): Promise<UsHts[]> => {
      try {
        const searchInput = {
          searchCriteria: {
            number: {
              operator: TextSearchOperator.STARTS_WITH,
              values: prefixes,
            },
          },
        };

        const response = await apolloClient.query<
          searchUsHtssQuery,
          searchUsHtssQueryVariables
        >({
          query: SEARCH_US_HTS,
          variables: { input: searchInput },
        });

        return (response?.data?.searchUsHtss?.results || []) as UsHts[];
      } catch (error) {
        reportError(error);
        return [];
      }
    },
    [],
  );

  const processSpreadsheetData = useCallback(async (): Promise<
    ProcessedTariffData[]
  > => {
    const rawData = extractSpreadsheetData(additionalTariffSpreadsheets);
    if (!rawData.length) {
      throw new Error('No spreadsheet data available');
    }

    setRawSpreadsheetData(rawData);

    // Get unique prefixes and query HTS records
    const uniquePrefixes = [...new Set(rawData.map(({ prefix }) => prefix))];
    const htsRecords = await searchHtsRecordsByPrefixes(uniquePrefixes);
    setHtsRecords(htsRecords);

    // Count HTS records per tariff
    const tariffCounts = new Map<string, number>();
    rawData.forEach(({ prefix, tariff }) => {
      const matchingCount = htsRecords.filter((record) =>
        record.number.startsWith(prefix),
      ).length;
      tariffCounts.set(tariff, (tariffCounts.get(tariff) || 0) + matchingCount);
    });

    return Array.from(tariffCounts.entries()).map(([tariff, count], index) => ({
      key: (index + 1).toString(),
      additionalNumber: tariff,
      count,
    }));
  }, [additionalTariffSpreadsheets, searchHtsRecordsByPrefixes]);

  const handleReviewStep = async () => {
    setIsLoading(true);
    try {
      void message.info(PROCESSING_SPREADSHEETS_MESSAGE, 3.0);

      const processed = await processSpreadsheetData();
      setProcessedData(processed);
      setCurrentStep(ImportAdditionalTariffSteps.REVIEW);

      if (processed.length === 0) {
        void message.warning(
          'No valid data found in the spreadsheet. Please check the file format.',
        );
      }
    } catch (e) {
      reportError(e);
      void message.error(
        'There was an error processing your spreadsheet, please try again later.',
        5,
      );
    }
    setIsLoading(false);
  };

  const updateHtsRecords = useCallback(async () => {
    let totalUpdated = 0;
    const allErrors: string[] = [];

    const uniqueTariffs = [
      ...new Set(rawSpreadsheetData.map(({ tariff }) => tariff)),
    ];

    for (const tariff of uniqueTariffs) {
      const matchingRecords = htsRecords.filter((record) =>
        rawSpreadsheetData.some(
          ({ prefix, tariff: recordTariff }) =>
            recordTariff === tariff && record.number.startsWith(prefix),
        ),
      );

      if (matchingRecords.length > 0) {
        // Get additional fields from the first matching record in spreadsheet data
        const tariffData = rawSpreadsheetData.find(
          ({ tariff: t }) => t === tariff,
        );
        const additionalFields = tariffData
          ? {
              type: tariffData.type,
              startDate: tariffData.startDate,
              description: tariffData.description,
              exportGroup: tariffData.exportGroup,
            }
          : undefined;

        const result = await updateHtsRecordsWithAdditionalTariff(
          matchingRecords,
          tariff,
          additionalFields,
        );
        totalUpdated += result.updatedCount;
        allErrors.push(...result.errors);
      }
    }

    return { totalUpdated, allErrors };
  }, [rawSpreadsheetData, htsRecords]);

  const handleSaveStep = async () => {
    if (!rawSpreadsheetData.length) {
      void message.error('No data available for import');
      return;
    }

    setIsLoading(true);
    setCurrentStep(ImportAdditionalTariffSteps.SAVE);
    setImportResult(null);
    void message.info(APPLYING_TARIFFS_MESSAGE, 5.0);

    try {
      const { totalUpdated, allErrors } = await updateHtsRecords();

      const result = {
        success: allErrors.length === 0,
        updatedCount: totalUpdated,
        errors: allErrors,
      };

      setImportResult(result);

      if (result.success) {
        void message.success(
          `Successfully updated ${result.updatedCount} HTS records!`,
          3.0,
        );
      } else {
        void message.error('Import failed. Check console for details.');
        reportError(new Error(`Import errors: ${result.errors.join(', ')}`));
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      reportError(e);
      void message.error(`Import failed: ${errorMessage}`, 5.0);

      setImportResult({
        success: false,
        updatedCount: 0,
        errors: [errorMessage],
      });
    } finally {
      setIsLoading(false);
    }
  };

  const nextButton = (
    <Button
      type="primary"
      disabled={!isPreviewReady}
      htmlType="submit"
      key="nextImportAdditionalTariffs"
      onClick={handleReviewStep}
      loading={isLoading}
    >
      Review
    </Button>
  );

  const importButton = (
    <Button
      type="primary"
      disabled={processedData.length === 0}
      key="saveAdditionalTariffs"
      onClick={handleSaveStep}
      loading={isLoading}
    >
      Save
    </Button>
  );

  const submitButton =
    currentStep === ImportAdditionalTariffSteps.UPLOAD
      ? nextButton
      : currentStep === ImportAdditionalTariffSteps.REVIEW
      ? importButton
      : null;

  return (
    <Modal
      key={'importAddtionalTariffs'}
      open={visible}
      width={720}
      title="Import Additional Tariffs"
      maskClosable={!isLoading}
      destroyOnClose={true}
      onCancel={() => {
        resetModalState();
        closeModal();
      }}
      footer={[
        <Button
          disabled={isLoading}
          key="cancelImportAddtionalTariffs"
          onClick={handleCancel}
        >
          {currentStep === ImportAdditionalTariffSteps.UPLOAD
            ? 'Cancel'
            : 'Go back and fix'}
        </Button>,
        submitButton,
      ].filter(Boolean)}
    >
      <StyledSteps current={currentStep} items={importSteps} size="small" />
      <div>
        {currentStep === ImportAdditionalTariffSteps.UPLOAD ? (
          <ImportAdditionalTariffUpload
            fileList={fileList}
            isLoading={isLoading}
            additionalTariffSpreadsheets={additionalTariffSpreadsheets}
            setFileList={setFileList}
            setIsLoading={setIsLoading}
            setAdditionalTariffSpreadsheets={setAdditionalTariffSpreadsheets}
            setIsPreviewReady={setIsPreviewReady}
          />
        ) : currentStep === ImportAdditionalTariffSteps.REVIEW ? (
          <ImportAdditionalTariffReview dataSource={processedData} />
        ) : currentStep === ImportAdditionalTariffSteps.SAVE ? (
          <SaveContainer>
            <SaveTitle>Save</SaveTitle>
            {!isLoading && !importResult ? (
              <>
                <ReadyText>Ready to apply</ReadyText>
                <DescriptionText>
                  This will apply additional tariffs to matching HTS records
                </DescriptionText>
              </>
            ) : isLoading ? (
              <LoadingContainer>
                <LoadingIcon>
                  <LoadingOutlined />
                </LoadingIcon>
                <p>Saving Additional tariffs...</p>
              </LoadingContainer>
            ) : importResult ? (
              <ResultContainer>
                {importResult.success ? (
                  <div>
                    <SuccessText>✅ Save successful</SuccessText>
                    <UpdatedCountText>
                      Records updated: {importResult.updatedCount}
                    </UpdatedCountText>
                    {importResult.errors.length > 0 ? (
                      <WarningText>
                        {importResult.errors.length} warnings occurred (check
                        console for details)
                      </WarningText>
                    ) : null}
                  </div>
                ) : (
                  <div>
                    <ErrorText>❌ Save failed</ErrorText>
                    <ErrorDetailsText>
                      Check console for error details
                    </ErrorDetailsText>
                  </div>
                )}
                <CloseButton type="primary" onClick={closeModal}>
                  Close
                </CloseButton>
              </ResultContainer>
            ) : null}
          </SaveContainer>
        ) : null}
      </div>
    </Modal>
  );
};

export default memo(ImportAdditionalTariffModal);
